//@ts-nocheck

/**
 * PWA Install Manager
 * Handles PWA installation prompts and guidance across different platforms
 */

const INSTALL_PROMPT_DISMISSED_KEY = 'pwa-install-prompt-dismissed';
const INSTALL_PROMPT_DISMISSED_DATE_KEY = 'pwa-install-prompt-dismissed-date';
const PWA_INSTALLED_KEY = 'pwa-installed';
const PROMPT_COOLDOWN_DAYS = 7;

export class PWAInstallManager {
  constructor() {
    this.deferredPrompt = null;
    this.isInstallable = false;
    this.isInstalled = this.checkIfInstalled();
    this.platform = this.detectPlatform();
    this.setupEventListeners();
  }

  /**
   * Setup event listeners for PWA install events
   */
  setupEventListeners() {
    // Listen for beforeinstallprompt event (Chrome/Edge)
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('[PWA] beforeinstallprompt event fired');
      e.preventDefault();
      this.deferredPrompt = e;
      this.isInstallable = true;

      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('pwaInstallable'));
    });

    // Listen for appinstalled event
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App was installed');
      this.isInstalled = true;
      localStorage.setItem(PWA_INSTALLED_KEY, 'true');

      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('pwaInstalled'));
    });

    // Check for display mode changes
    window
      .matchMedia('(display-mode: standalone)')
      .addEventListener('change', (e) => {
        if (e.matches) {
          this.isInstalled = true;
          localStorage.setItem(PWA_INSTALLED_KEY, 'true');
        }
      });
  }

  /**
   * Detect platform and browser
   */
  detectPlatform() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    // iOS detection
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isIOSChrome = isIOS && /CriOS/.test(userAgent);
    const isIOSSafari =
      isIOS && /Safari/.test(userAgent) && !/CriOS/.test(userAgent);

    // Android detection
    const isAndroid = /android/i.test(userAgent);
    const isAndroidChrome =
      isAndroid && /Chrome/.test(userAgent) && !/Edge/.test(userAgent);
    const isAndroidSamsung = isAndroid && /SamsungBrowser/.test(userAgent);

    // Desktop detection
    const isDesktop = !isIOS && !isAndroid;
    const isDesktopChrome =
      isDesktop && /Chrome/.test(userAgent) && !/Edge/.test(userAgent);
    const isDesktopEdge = isDesktop && /Edg/.test(userAgent);

    // Determine platform
    if (isIOSSafari) return 'ios-safari';
    if (isIOSChrome) return 'ios-chrome';
    if (isAndroidSamsung) return 'android-samsung';
    if (isAndroidChrome) return 'android-chrome';
    if (isAndroid) return 'android-generic';
    if (isDesktopEdge) return 'desktop-edge';
    if (isDesktopChrome) return 'desktop-chrome';
    if (isDesktop) return 'desktop-generic';

    return 'unknown';
  }

  /**
   * Check if app is already installed/running in standalone mode
   */
  checkIfInstalled() {
    // Check if running in standalone mode
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');

    // Check localStorage
    const wasInstalled = localStorage.getItem(PWA_INSTALLED_KEY) === 'true';

    return isStandalone || wasInstalled;
  }

  /**
   * Check if platform supports native install prompt
   */
  supportsNativeInstall() {
    return [
      'android-chrome',
      'android-samsung',
      'desktop-chrome',
      'desktop-edge'
    ].includes(this.platform);
  }

  /**
   * Check if platform supports manual install instructions
   */
  supportsManualInstall() {
    return ['ios-safari', 'ios-chrome', 'android-generic'].includes(
      this.platform
    );
  }

  /**
   * Check if user is eligible for install prompt
   */
  isEligibleForPrompt() {
    // Don't show if already installed
    if (this.isInstalled) return false;

    // Check if user dismissed recently
    const dismissedDate = localStorage.getItem(
      INSTALL_PROMPT_DISMISSED_DATE_KEY
    );
    if (dismissedDate) {
      const daysSinceDismissed =
        (Date.now() - parseInt(dismissedDate)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < PROMPT_COOLDOWN_DAYS) {
        return false;
      }
    }

    // Check if permanently dismissed
    const isDismissed =
      localStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY) === 'true';
    if (isDismissed && !dismissedDate) {
      return false; // Permanently dismissed
    }

    return true;
  }

  /**
   * Trigger native install prompt (Chrome/Edge)
   */
  async triggerNativeInstall() {
    if (!this.deferredPrompt) {
      console.warn('[PWA] No deferred prompt available');
      return { outcome: 'not-available' };
    }

    try {
      // Show the install prompt
      this.deferredPrompt.prompt();

      // Wait for user response
      const choiceResult = await this.deferredPrompt.userChoice;
      console.log('[PWA] User choice:', choiceResult.outcome);

      if (choiceResult.outcome === 'accepted') {
        this.isInstalled = true;
        localStorage.setItem(PWA_INSTALLED_KEY, 'true');
      }

      // Clear the deferred prompt
      this.deferredPrompt = null;
      this.isInstallable = false;

      return choiceResult;
    } catch (error) {
      console.error('[PWA] Error showing install prompt:', error);
      return { outcome: 'error', error };
    }
  }

  /**
   * Get install instructions for current platform
   */
  getInstallInstructions() {
    const instructions = {
      'ios-safari': {
        title: 'To save this trip…',
        steps: [
          { icon: 'Share', text: 'Tap the Share icon (bottom center)' },
          { icon: 'Plus', text: 'Scroll and choose "Add to Home Screen"' },
          { icon: 'Check', text: 'Tap "Add" (top right)' }
        ]
      },
      'ios-chrome': {
        title: 'To save this trip…',
        steps: [
          { icon: 'Share', text: 'Tap the Share icon (top right)' },
          { icon: 'Plus', text: 'Scroll and choose "Add to Home Screen"' },
          { icon: 'Check', text: 'Tap "Add" (top right)' }
        ]
      },
      'android-chrome': {
        title: 'To save this trip…',
        steps: [
          { icon: 'DotsThree', text: 'Tap the three dots (top right)' },
          { icon: 'Plus', text: 'Tap "Add to Home screen"' },
          { icon: 'Check', text: 'Tap "Add"' }
        ]
      },
      'android-samsung': {
        title: 'To save this trip…',
        steps: [
          { icon: 'List', text: 'Tap the menu (bottom right)' },
          { icon: 'Plus', text: 'Tap "Add page to" → "Home screen"' },
          { icon: 'Check', text: 'Confirm' }
        ]
      },
      'android-generic': {
        title: 'To save this trip…',
        steps: [
          { icon: 'DotsThree', text: "Tap your browser's menu button" },
          { icon: 'Plus', text: 'Tap "Add to Home screen"' },
          { icon: 'Check', text: 'Tap "Add"' }
        ]
      },
      'desktop-chrome': {
        title: 'To install this app…',
        steps: [
          { icon: 'DotsThree', text: 'Click the three dots (top right)' },
          { icon: 'Download', text: 'Select "Install [App Name]"' }
        ]
      },
      'desktop-edge': {
        title: 'To install this app…',
        steps: [
          { icon: 'DotsThree', text: 'Click the three dots (top right)' },
          {
            icon: 'Download',
            text: 'Select "Apps" → "Install this site as an app"'
          }
        ]
      },
      'desktop-generic': {
        title: 'To install this app…',
        steps: [
          { icon: 'DotsThree', text: "Use your browser's menu" },
          {
            icon: 'Download',
            text: 'Look for "Install app" or "Add to Home screen"'
          }
        ]
      }
    };

    return instructions[this.platform] || instructions['android-generic'];
  }

  /**
   * Dismiss install prompt
   */
  dismissPrompt(temporary = true) {
    if (temporary) {
      localStorage.setItem(
        INSTALL_PROMPT_DISMISSED_DATE_KEY,
        Date.now().toString()
      );
      localStorage.removeItem(INSTALL_PROMPT_DISMISSED_KEY);
    } else {
      localStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, 'true');
      localStorage.removeItem(INSTALL_PROMPT_DISMISSED_DATE_KEY);
    }
  }

  /**
   * Get platform info for debugging
   */
  getPlatformInfo() {
    return {
      platform: this.platform,
      isInstallable: this.isInstallable,
      isInstalled: this.isInstalled,
      supportsNativeInstall: this.supportsNativeInstall(),
      supportsManualInstall: this.supportsManualInstall(),
      isEligibleForPrompt: this.isEligibleForPrompt()
    };
  }
}

// Export singleton instance
export const pwaInstallManager = new PWAInstallManager();
